import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { EditOnly } from '@/components/EditOnly';
import {
  getCredentials, createCredential, updateCredential, deleteCredential, revealCredential,
  getCredentialTotp, isRevealPending,
} from '@/api/credentials';
import type {
  PartnerCredential, CreateCredentialInput, CredentialSecret, CredentialTotpCode,
} from '@/api/types';
import { formatTotp, normalizeTotpSecret, totpCode } from '@/lib/totp';

type ModalMode = 'create' | 'edit' | 'delete' | null;

const emptyForm: CreateCredentialInput = {
  name: '', login: '', password: '', email: '', url: '', tg_chat: '', comment: '', totp_secret: '',
};

// An open code refreshes itself on every 30 s rollover; each refresh is an
// audited view, so it closes on its own after a while instead of ticking all day.
const CODE_AUTO_HIDE_MS = 3 * 60 * 1000;

interface ShownCode extends CredentialTotpCode {
  /** ms at which this code rolls over and the next one is fetched. */
  rollsAt: number;
  openedAt: number;
}

/** Live code for the key being typed, so the site asking "enter the code to
 *  turn on 2FA" can be answered before the key is even saved. */
function TotpPreview({ value }: { value: string }) {
  const seed = value.trim() ? normalizeTotpSecret(value) : null;
  const [code, setCode] = useState('');
  const [left, setLeft] = useState(30);
  useEffect(() => {
    if (!seed) return;
    let alive = true;
    const tick = () => {
      const now = Date.now();
      setLeft(30 - (Math.floor(now / 1000) % 30));
      totpCode(seed, now).then((c) => { if (alive) setCode(c); });
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => { alive = false; clearInterval(t); };
  }, [seed]);

  if (!value.trim()) return null;
  if (!seed) {
    return <p className="text-xs text-danger mt-1">Not a 2FA key: paste the base32 key or the otpauth:// link (6 digits, 30 s).</p>;
  }
  return (
    <p className="text-xs text-content-muted mt-1">
      Current code: <span className="font-mono text-content text-sm">{code ? formatTotp(code) : '…'}</span>
      <span className="text-content-subtle"> · {left}s</span>
      {code && (
        <button
          type="button"
          onClick={() => navigator.clipboard.writeText(code)}
          className="ml-2 text-primary hover:underline"
        >
          copy
        </button>
      )}
    </p>
  );
}

export function Credentials() {
  const qc = useQueryClient();
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['credentials'],
    queryFn: getCredentials,
  });

  const [mode, setMode] = useState<ModalMode>(null);
  const [active, setActive] = useState<PartnerCredential | null>(null);
  const [form, setForm] = useState<CreateCredentialInput>(emptyForm);
  const [error, setError] = useState('');
  // revealed secrets by credential id (present => shown)
  const [revealed, setRevealed] = useState<Record<string, CredentialSecret>>({});
  const [revealing, setRevealing] = useState<string | null>(null);
  // Credentials whose reveal is waiting for a super admin. There is no polling:
  // the next click on the same button is the check.
  const [awaitingApproval, setAwaitingApproval] = useState<Record<string, true>>({});
  // 2FA codes on screen, by credential id; gated on their own approval.
  const [codes, setCodes] = useState<Record<string, ShownCode>>({});
  const [awaitingCode, setAwaitingCode] = useState<Record<string, true>>({});
  const [fetchingCode, setFetchingCode] = useState<string | null>(null);
  const codeInFlight = useRef(new Set<string>());
  const [now, setNow] = useState(() => Date.now());
  const [removeTotp, setRemoveTotp] = useState(false);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['credentials'] });

  const createM = useMutation({
    mutationFn: createCredential,
    onSuccess: () => { invalidate(); setMode(null); },
    onError: (e: Error) => setError(e.message),
  });
  const updateM = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateCredentialInput }) => updateCredential(id, data),
    onSuccess: (_data, variables) => {
      invalidate();
      setMode(null);
      // The stored secret may have changed — drop any stale revealed value for this row.
      setRevealed((p) => { const n = { ...p }; delete n[variables.id]; return n; });
      setCodes((p) => { const n = { ...p }; delete n[variables.id]; return n; });
    },
    onError: (e: Error) => setError(e.message),
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => deleteCredential(id),
    onSuccess: () => { invalidate(); setMode(null); },
    onError: (e: Error) => setError(e.message),
  });

  const openCreate = () => { setForm(emptyForm); setError(''); setActive(null); setRemoveTotp(false); setMode('create'); };
  const openEdit = (r: PartnerCredential) => {
    setActive(r);
    setForm({
      name: r.name, login: r.login ?? '', password: '', email: '',
      url: r.url ?? '', tg_chat: r.tg_chat ?? '', comment: r.comment ?? '', totp_secret: '',
    });
    setRemoveTotp(false);
    setError('');
    setMode('edit');
  };
  const openDelete = (r: PartnerCredential) => { setActive(r); setError(''); setMode('delete'); };

  const submit = () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    const totp = form.totp_secret?.trim() ?? '';
    if (totp && !removeTotp && !normalizeTotpSecret(totp)) { setError('The 2FA key is not valid'); return; }
    if (mode === 'create') createM.mutate(form);
    else if (mode === 'edit' && active) {
      // Only send secret fields when non-empty, so blank inputs don't wipe them.
      const data: CreateCredentialInput = { ...form };
      if (!data.password) delete (data as Partial<CreateCredentialInput>).password;
      if (!data.email) delete (data as Partial<CreateCredentialInput>).email;
      if (removeTotp) data.totp_secret = '';
      else if (!totp) delete (data as Partial<CreateCredentialInput>).totp_secret;
      updateM.mutate({ id: active.id, data });
    }
  };

  const toggleReveal = async (r: PartnerCredential) => {
    if (revealed[r.id]) {
      setRevealed((p) => { const n = { ...p }; delete n[r.id]; return n; });
      return;
    }
    setRevealing(r.id);
    try {
      const res = await revealCredential(r.id);
      if (isRevealPending(res)) {
        // The toast in the API client already explains what happened; the row
        // only has to stop looking like a failed click.
        setAwaitingApproval((p) => ({ ...p, [r.id]: true }));
        return;
      }
      setAwaitingApproval((p) => { const n = { ...p }; delete n[r.id]; return n; });
      setRevealed((p) => ({ ...p, [r.id]: res }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reveal data');
    } finally {
      setRevealing(null);
    }
  };

  const dropCode = (id: string) => setCodes((p) => { const n = { ...p }; delete n[id]; return n; });

  const loadCode = async (id: string, openedAt = Date.now()) => {
    if (codeInFlight.current.has(id)) return;
    codeInFlight.current.add(id);
    setFetchingCode(id);
    try {
      const res = await getCredentialTotp(id);
      if (isRevealPending(res)) {
        dropCode(id);
        setAwaitingCode((p) => ({ ...p, [id]: true }));
        return;
      }
      setAwaitingCode((p) => { const n = { ...p }; delete n[id]; return n; });
      setCodes((p) => ({ ...p, [id]: { ...res, rollsAt: Date.now() + res.seconds_left * 1000, openedAt } }));
    } catch (e) {
      dropCode(id);
      setError(e instanceof Error ? e.message : 'Failed to get the 2FA code');
    } finally {
      codeInFlight.current.delete(id);
      setFetchingCode((cur) => (cur === id ? null : cur));
    }
  };

  // One clock for every open code: the countdown, the rollover refetch, and
  // closing it before the approval window does — a refetch past the window
  // would file a new Telegram request nobody asked for.
  const anyCode = Object.keys(codes).length > 0;
  useEffect(() => {
    if (!anyCode) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [anyCode]);
  useEffect(() => {
    for (const [id, c] of Object.entries(codes)) {
      if (now < c.rollsAt) continue;
      const grantOver = c.grant_expires_at != null && now >= c.grant_expires_at * 1000;
      if (grantOver || now - c.openedAt >= CODE_AUTO_HIDE_MS) dropCode(id);
      else void loadCode(id, c.openedAt);
    }
    // loadCode is stable enough: it only reads refs and setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [now, codes]);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-content">Credentials</h1>
        <EditOnly>
          <Button onClick={openCreate}>Add</Button>
        </EditOnly>
      </div>

      {mode === null && error && <p className="text-danger text-sm mb-3">{error}</p>}

      {isLoading ? (
        <p className="text-content-muted text-sm">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-content-muted text-sm">No partners yet. Click “Add”.</p>
      ) : (
        <div className="overflow-x-auto border border-border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted text-content-muted">
              <tr>
                {['Partner', 'Login', 'Password', 'Email', '2FA', 'Link', 'TG chat', 'Comment', ''].map((h) => (
                  <th key={h} className="text-left font-medium px-3 py-2 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const sec = revealed[r.id];
                const code = codes[r.id];
                return (
                  <tr key={r.id} className="border-t border-border align-top">
                    <td className="px-3 py-2 font-medium text-content whitespace-nowrap">{r.name}</td>
                    <td className="px-3 py-2 text-content-muted">{r.login || '—'}</td>
                    <td className="px-3 py-2 font-mono">
                      {sec ? (
                        <span className="text-content">
                          {sec.password || '—'}
                          {sec.password && (
                            <button
                              onClick={() => navigator.clipboard.writeText(sec.password!)}
                              className="ml-2 text-xs text-primary hover:underline"
                              title="Copy password"
                            >
                              copy
                            </button>
                          )}
                        </span>
                      ) : awaitingApproval[r.id] ? (
                        <span className="text-warning text-xs font-sans" title="A super admin has to approve the request in Telegram">
                          ⏳ waiting for approval
                        </span>
                      ) : (
                        <span className="text-content-subtle">{r.has_password ? '••••••' : '—'}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono">
                      {sec ? (
                        <span className="text-content">
                          {sec.email || '—'}
                          {sec.email && (
                            <button
                              onClick={() => navigator.clipboard.writeText(sec.email!)}
                              className="ml-2 text-xs text-primary hover:underline"
                              title="Copy email"
                            >
                              copy
                            </button>
                          )}
                        </span>
                      ) : (
                        <span className="text-content-subtle">{r.has_email ? '••••••' : '—'}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono whitespace-nowrap">
                      {!r.has_totp ? (
                        <span className="text-content-subtle">—</span>
                      ) : code ? (
                        <span className="text-content">
                          {formatTotp(code.code)}
                          <span className="ml-1 text-xs font-sans text-content-subtle">
                            {Math.max(0, Math.ceil((code.rollsAt - now) / 1000))}s
                          </span>
                          <button
                            onClick={() => navigator.clipboard.writeText(code.code)}
                            className="ml-2 text-xs font-sans text-primary hover:underline"
                            title="Copy code"
                          >
                            copy
                          </button>
                          <button
                            onClick={() => dropCode(r.id)}
                            className="ml-2 text-xs font-sans text-content-muted hover:text-content"
                          >
                            hide
                          </button>
                        </span>
                      ) : (
                        <span className="font-sans text-xs">
                          {awaitingCode[r.id] && (
                            <span className="text-warning mr-2" title="A super admin has to approve the request in Telegram">
                              ⏳ waiting for approval
                            </span>
                          )}
                          <button
                            onClick={() => loadCode(r.id)}
                            disabled={fetchingCode === r.id}
                            className="text-primary hover:underline disabled:opacity-50"
                          >
                            {fetchingCode === r.id ? '…' : awaitingCode[r.id] ? 'Check approval' : 'Code'}
                          </button>
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-content-muted max-w-[200px] truncate">
                      {r.url ? <a href={r.url} target="_blank" rel="noreferrer" className="text-primary hover:underline">{r.url}</a> : '—'}
                    </td>
                    <td className="px-3 py-2 text-content-muted whitespace-nowrap">{r.tg_chat || '—'}</td>
                    <td className="px-3 py-2 text-content-muted max-w-[220px]">{r.comment || '—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-right">
                      {(r.has_password || r.has_email) && (
                        <>
                          {sec?.grant_expires_at != null && (
                            <span className="text-content-subtle text-xs mr-2">
                              until {new Date(sec.grant_expires_at * 1000).toISOString().slice(11, 16)} UTC
                            </span>
                          )}
                          <button
                            onClick={() => toggleReveal(r)}
                            disabled={revealing === r.id}
                            className="text-primary hover:underline disabled:opacity-50 mr-3"
                          >
                            {sec ? 'Hide'
                              : revealing === r.id ? '…'
                                : awaitingApproval[r.id] ? 'Check approval'
                                  : 'Show'}
                          </button>
                        </>
                      )}
                      <EditOnly>
                        <button onClick={() => openEdit(r)} className="text-content-muted hover:text-content mr-3">Edit</button>
                        <button onClick={() => openDelete(r)} className="text-danger hover:underline">Delete</button>
                      </EditOnly>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(mode === 'create' || mode === 'edit') && (
        <Modal isOpen onClose={() => setMode(null)} title={mode === 'create' ? 'New partner' : 'Edit'}>
          <div className="space-y-3">
            {([
              ['name', 'Name *'], ['login', 'Login'], ['password', 'Password'],
              ['email', 'Email'], ['totp_secret', '2FA key'],
              ['url', 'Link'], ['tg_chat', 'Telegram chat'], ['comment', 'Comment'],
            ] as Array<[keyof CreateCredentialInput, string]>).map(([key, label]) => (
              <div key={key}>
                <label className="block text-xs font-medium text-content-muted mb-1">
                  {label}
                  {mode === 'edit' && (key === 'password' || key === 'email'
                    || (key === 'totp_secret' && active?.has_totp)) && (
                    <span className="text-content-subtle"> (leave blank to keep unchanged)</span>
                  )}
                </label>
                {key === 'comment' ? (
                  <textarea
                    className="w-full px-2 py-1.5 rounded-md border border-border bg-surface text-sm"
                    rows={2}
                    value={form[key] ?? ''}
                    onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
                  />
                ) : key === 'totp_secret' ? (
                  <>
                    <input
                      type="text"
                      autoComplete="off"
                      spellCheck={false}
                      placeholder="Key from the site's 2FA setup, or the otpauth:// link"
                      disabled={removeTotp}
                      className="w-full px-2 py-1.5 rounded-md border border-border bg-surface text-sm font-mono disabled:opacity-50"
                      value={form.totp_secret ?? ''}
                      onChange={(e) => setForm((p) => ({ ...p, totp_secret: e.target.value }))}
                    />
                    {!removeTotp && <TotpPreview value={form.totp_secret ?? ''} />}
                    {mode === 'edit' && active?.has_totp && (
                      <label className="flex items-center gap-2 mt-1 text-xs text-content-muted">
                        <input type="checkbox" checked={removeTotp} onChange={(e) => setRemoveTotp(e.target.checked)} />
                        Remove 2FA key
                      </label>
                    )}
                  </>
                ) : (
                  <input
                    type="text"
                    className="w-full px-2 py-1.5 rounded-md border border-border bg-surface text-sm"
                    value={form[key] ?? ''}
                    onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
                  />
                )}
              </div>
            ))}
            {error && <p className="text-danger text-sm">{error}</p>}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setMode(null)}>Cancel</Button>
              <Button onClick={submit} disabled={createM.isPending || updateM.isPending}>Save</Button>
            </div>
          </div>
        </Modal>
      )}

      {mode === 'delete' && active && (
        <Modal isOpen onClose={() => setMode(null)} title="Delete partner">
          <p className="text-sm text-content-muted mb-4">Delete “{active.name}”? This action cannot be undone.</p>
          {error && <p className="text-danger text-sm mb-2">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setMode(null)}>Cancel</Button>
            <Button variant="danger" onClick={() => deleteM.mutate(active.id)} disabled={deleteM.isPending}>Delete</Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default Credentials;
